/**
 * /account/security — self-service security console for the
 * authenticated user.
 *
 * Surfaces three things the spec calls for:
 *   * Active sessions list with "this device" indicator + log-out
 *     buttons (others / everywhere)
 *   * Recent login activity (last 10 events)
 *   * Quick link back to the in-Settings password/MFA controls
 *
 * The MFA + password-change UI itself stays in
 * Settings → Security → SecuritySettings; we deep-link to it from
 * here so the auth controls have a single source of truth.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  ExternalLink,
  Loader2,
  LogOut,
  Monitor,
  ShieldCheck,
} from "lucide-react";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/hooks/useToast";
import { formatDate, pageTransition } from "@/lib/utils";
import {
  getMyLoginEvents,
  getMySessions,
  logoutMyAllSessions,
  logoutMyOtherSessions,
  type LoginEventRow,
  type LoginEventType,
  type SessionRow,
} from "@/api/security";
import { TwoFactorCard } from "@/components/auth/TwoFactorCard";

const EVENT_LABELS: Record<LoginEventType, string> = {
  login_success: "Sign in",
  login_failed: "Failed sign in",
  logout: "Sign out",
  password_changed: "Password changed",
  password_reset_requested: "Password reset requested",
  password_reset_completed: "Password reset completed",
  account_locked: "Account locked",
  account_unlocked: "Account unlocked",
  session_force_logout: "Force logout by admin",
};

export function AccountSecurityPage() {
  const { addToast } = useToast();
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [events, setEvents] = useState<LoginEventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([
        getMySessions(),
        getMyLoginEvents(10),
      ]);
      setSessions(s);
      setEvents(e);
    } catch {
      addToast("error", "Couldn't load your security info");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logoutOthers = async () => {
    if (!confirm("Sign out every other device?")) return;
    setBusy(true);
    try {
      await logoutMyOtherSessions();
      addToast("success", "Other sessions signed out");
      load();
    } catch {
      addToast("error", "Couldn't sign out other sessions");
    } finally {
      setBusy(false);
    }
  };

  const logoutAll = async () => {
    if (
      !confirm(
        "Sign out everywhere — including this device? You'll need to log back in.",
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await logoutMyAllSessions();
      // Server has already revoked our refresh token; force a redirect.
      window.location.href = "/login";
    } catch {
      addToast("error", "Couldn't sign out");
      setBusy(false);
    }
  };

  return (
    <motion.div {...pageTransition} className="mx-auto max-w-3xl">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold font-heading text-text-primary flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-accent" />
          Security
        </h2>
        <p className="text-sm text-text-muted mt-1">
          Manage your active sessions and review your account&rsquo;s
          recent activity.
        </p>
      </div>

      {/* 2FA self-service — setup, status, backup-code regeneration,
          disable. Drives its own state independently of sessions/events. */}
      <TwoFactorCard className="mb-6" />

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Active sessions</CardTitle>
          <Link
            to="/settings?tab=security"
            className="text-xs text-accent hover:underline inline-flex items-center gap-1"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Change password / MFA
          </Link>
        </CardHeader>

        {loading ? (
          <div className="py-10 text-center text-text-muted">
            <Loader2 className="h-6 w-6 animate-spin mx-auto" />
          </div>
        ) : sessions.length === 0 ? (
          <p className="text-sm text-text-muted">No active sessions found.</p>
        ) : (
          <ul className="divide-y divide-border/40">
            {sessions.map((s) => (
              <li
                key={s.id}
                className="py-3 flex items-center justify-between gap-3"
              >
                <div className="flex items-start gap-2 min-w-0">
                  <Monitor className="h-4 w-4 text-text-muted mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm text-text-primary">
                      {s.device_info || "Unknown device"}
                      {s.is_current && (
                        <span className="ml-2 text-xs text-success font-medium">
                          This device
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-text-muted">
                      {s.ip_address ? `${s.ip_address} · ` : ""}
                      {s.last_active_at
                        ? `Last active ${formatDate(s.last_active_at)}`
                        : "Never refreshed"}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-4 flex gap-2 flex-wrap">
          <Button
            variant="secondary"
            size="sm"
            onClick={logoutOthers}
            disabled={busy || sessions.length <= 1}
          >
            <LogOut className="h-4 w-4" />
            Log out other sessions
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={logoutAll}
            disabled={busy}
          >
            <LogOut className="h-4 w-4" />
            Log out everywhere
          </Button>
        </div>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent activity</CardTitle>
        </CardHeader>
        {loading ? (
          <div className="py-10 text-center text-text-muted">
            <Loader2 className="h-6 w-6 animate-spin mx-auto" />
          </div>
        ) : events.length === 0 ? (
          <p className="text-sm text-text-muted">
            Nothing recorded yet.
          </p>
        ) : (
          <ul className="divide-y divide-border/40">
            {events.map((e) => (
              <li
                key={e.id}
                className={`py-3 flex items-center justify-between gap-3 ${
                  e.event_type === "login_failed" ||
                  e.event_type === "account_locked"
                    ? "text-danger"
                    : ""
                }`}
              >
                <div className="min-w-0">
                  <p className="text-sm text-text-primary flex items-center gap-2">
                    {(e.event_type === "login_failed" ||
                      e.event_type === "account_locked" ||
                      e.event_type === "session_force_logout") && (
                      <AlertTriangle className="h-3.5 w-3.5 text-danger" />
                    )}
                    {EVENT_LABELS[e.event_type] ?? e.event_type}
                  </p>
                  <p className="text-xs text-text-muted">
                    {e.device_info || "Unknown device"}
                    {e.ip_address ? ` · ${e.ip_address}` : ""}
                  </p>
                </div>
                <span className="text-xs text-text-muted whitespace-nowrap">
                  {formatDate(e.created_at)}
                </span>
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-text-muted mt-3">
          See something suspicious?{" "}
          <Link to="/help" className="text-accent hover:underline">
            Contact your administrator
          </Link>
          .
        </p>
      </Card>
    </motion.div>
  );
}

export default AccountSecurityPage;
